import React from "react";
import { Link } from "react-router-dom";
import { Building2, ClipboardCheck, HardHat, Home as HomeIcon, LineChart, Compass, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: HomeIcon,
    title: "Residence Management",
    tag: "Core Service",
    body: "End-to-end management of student accommodation buildings — from letting, tenant vetting and collections to housekeeping, security oversight and community programming.",
    bullets: ["Leasing & vetting", "Rent collection", "Maintenance & inspections", "24/7 tenant support", "Compliance & reporting"],
  },
  {
    icon: LineChart,
    title: "Property Evaluations",
    tag: "Advisory",
    body: "Independent valuations and yield analysis for existing residences, acquisition targets and development sites — the type of work funders and boards trust.",
    bullets: ["Market valuations", "Yield & feasibility studies", "Portfolio benchmarking", "Acquisition due-diligence"],
  },
  {
    icon: ClipboardCheck,
    title: "Quantity Surveying",
    tag: "Technical",
    body: "Cost planning and financial control for new builds, refurbishments and fit-outs. We keep budgets honest and construction on track.",
    bullets: ["Cost planning", "Tender documentation", "Payment certifications", "Final accounts"],
  },
  {
    icon: HardHat,
    title: "Structural Engineering",
    tag: "Technical",
    body: "Structural design, assessment and reporting delivered by our accredited engineering partners — for both new developments and older buildings.",
    bullets: ["Structural assessments", "Design & drawings", "Site inspections", "Compliance reporting"],
  },
  {
    icon: Compass,
    title: "Architectural Services",
    tag: "Technical",
    body: "Concept, planning, and full architectural documentation for student residences — designed with occupancy economics in mind.",
    bullets: ["Concept design", "Council submissions", "Working drawings", "Site coordination"],
  },
  {
    icon: Building2,
    title: "Property Sales",
    tag: "Transactions",
    body: "Buying or selling a student residence, block of flats or development site? We source, market and advise across the transaction lifecycle.",
    bullets: ["Buildings & blocks", "Buy-to-let flats", "Development sites", "Off-market opportunities"],
  },
];

export default function ServicesPage() {
  return (
    <div>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32 pb-16">
        <p className="overline text-secondary">Our Services</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif leading-tight max-w-4xl">
          Everything a student accommodation building needs — under one roof.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
          Campusco delivers residence management, evaluations, quantity surveying,
          architectural and structural engineering services, and property sales
          across South Africa. Landlords, funders and developers pick the mix
          they need.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild className="rounded-sm bg-primary text-primary-foreground h-11 px-6">
            <Link to="/book-appointment" data-testid="services-book-btn">Book a Detailed Presentation</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-sm h-11 px-6">
            <Link to="/brochure" target="_blank" rel="noopener" data-testid="services-download-btn">View Brochure</Link>
          </Button>
        </div>
      </section>

      {/* Service list */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="space-y-px bg-border border border-border">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="bg-background grid lg:grid-cols-12 gap-8 p-8 md:p-12 items-start">
                <div className="lg:col-span-1">
                  <span className="font-serif text-3xl text-muted-foreground">0{i + 1}</span>
                </div>
                <div className="lg:col-span-3">
                  <Icon className="w-8 h-8 text-secondary" strokeWidth={1.5} />
                  <p className="mt-4 overline text-secondary">{s.tag}</p>
                  <h3 className="mt-2 font-serif text-2xl md:text-3xl">{s.title}</h3>
                </div>
                <div className="lg:col-span-5">
                  <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
                <div className="lg:col-span-3">
                  <ul className="space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="text-sm flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-secondary" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA banner */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <ShieldCheck className="w-8 h-8 text-secondary mb-6" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl md:text-4xl">Worried about NSFAS accreditation? We can assist.</h2>
            <p className="mt-4 text-primary-foreground/80 max-w-2xl">
              We help landlords and residence owners navigate NSFAS accreditation requirements — from documentation to
              compliance — so your building qualifies for funded student tenants.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Button asChild className="rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12">
              <Link to="/contact" data-testid="services-nsfas-contact">Talk to Us <ArrowRight className="ml-1 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
